import type {
	SRT_ColumnFiltersState,
	SRT_Row,
	SRT_RowSelectionState,
	SRT_SortingState,
	SRT_TableOptions,
} from '@/shared/components/master-table'
import type { OptimizationStatus, PricingEngineType } from '@/shared/generated'

import React, { useMemo, useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ogNameAndPcName } from '@/entities/pricing-campaign'

import { useMasterReactTable } from '@/shared/components/master-table'
import { Button } from '@/shared/components/ui/button'
import { getMasterTableLocale } from '@/shared/i18n'

import { getCamapignsPickerColumns } from './getCamapignsPickerColumns'

export type PricingCampaignDataRow = {
	id: string
	name: string
	engine: PricingEngineType
	optimization_group_name?: string
	optimization_group_id: string
	optimization_status?: OptimizationStatus
	products_count?: number
}
export const usePricingCampaignsPickerTable = ({
	isLoading,
	options,
	data,
	rowSelection,
	setRowSelection,
	columnVisibility = {},
	columnFilters,
	...rest
}: {
	columnVisibility?: Record<string, boolean>
	isLoading: boolean
	options: Record<string, { label: string; value: string }[]>
	data: PricingCampaignDataRow[]
	rowSelection: SRT_RowSelectionState
	setRowSelection: React.Dispatch<React.SetStateAction<SRT_RowSelectionState>>
	columnFilters?: SRT_ColumnFiltersState
} & Pick<SRT_TableOptions<PricingCampaignDataRow>, 'enableRowSelection'>) => {
	const { t } = useTranslation()

	const columns = useMemo(
		() => getCamapignsPickerColumns({ options }, t),
		[options, t]
	)
	const [sorting, setSorting] = useState<SRT_SortingState>([
		{
			desc: false,
			id: 'name',
		},
	])

	return useMasterReactTable<PricingCampaignDataRow>({
		localization: getMasterTableLocale(),
		data,
		columns,
		state: {
			isLoading,
			sorting,
			rowSelection,
		},
		initialState: {
			expanded: true,
			columnVisibility: {
				...columnVisibility,
				'srt-row-expand': false,
			},
			grouping: ['optimization_group_name'],
			columnFilters,
		},
		// Select only the children rows
		enableRowSelection(row) {
			return !!row.parentId
		},
		divideX: false,
		enableGrouping: true,
		enableColumnActions: false,
		groupedColumnMode: 'remove',
		enableColumnPinning: false,
		enableHeaderTooltip: false,
		enableSorting: true,
		enableColumnFilters: true,
		enableVirtualization: true,
		layoutMode: 'grid',
		getRowId: (row) => row.id,
		onRowSelectionChange: setRowSelection,
		sortingFns: {
			ogNameAndPcName,
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		defaultRowHeight: 36,
		getRowHeight(row) {
			return row?.depth === 0 ? 56 : 36
		},
		renderGroup: ({ row }) => <TableOptimzationGroupGroup row={row} />,
		onSortingChange: setSorting,
		...rest,
	})
}

function TableOptimzationGroupGroup({
	row,
}: {
	row: SRT_Row<PricingCampaignDataRow>
}) {
	const { t } = useTranslation()

	const name =
		row.original.optimization_group_name ||
		t('global', { defaultValue: 'Global' })

	return (
		<div className='flex h-14 items-start gap-2 pt-2'>
			<Button
				variant='ghost'
				size='icon-sm'
				onClick={row.getToggleExpandedHandler()}
			>
				{row.getIsExpanded() ? <ChevronDownIcon /> : <ChevronRightIcon />}
			</Button>
			<div className='flex flex-col gap-1'>
				<h5 className='font-bold'>{name}</h5>
				<div className='flex items-center gap-2 text-xs text-muted-foreground'>
					<span>
						{row.subRows?.length} {t('general_pc')}
					</span>
				</div>
			</div>
		</div>
	)
}
